import { format } from 'date-fns';
import IMask from 'imask';
import Big from 'big.js';

/**
 * Formatting currency to BRL pattern
 *
 * @param {number} value
 * @return {*}  {string}
 */
export const maskCurrencyFormat = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

/**
 * Formatting number to pattern 0,00
 *
 * @param {number} value
 * @return {*}  {string}
 */
export const maskDecimalNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
  }).format(value);
};

export const unmaskNumber = (num: string | null | undefined): string => {
  if (typeof num !== 'string') {
    return '';
  }

  return num.replace(/[^\d]+/g, '');
};

export const INPUT_MASKS = {
  CPF: '000.000.000-00',
  CPNJ: '00.000.000/0000-00',
  PHONE_NUMBER: '(00) 00000-0000',
  RESIDENTIAL_PHONE_NUMBER: '(00) 0000-0000',
  ZIPCODE: '[00000]{-}[000]',
  CREDIT_CARD: '0000-0000-0000-0000-000',
  CREDIT_CARD_EXPIRES: '00/0000',
  HOUR: '00:00',
};

export const maskDateFormat = (date: Date) => {
  return new Date(date).toISOString().slice(0, 10);
};

export const cpfMask = IMask.createMask({
  mask: INPUT_MASKS.CPF,
});

export const cnpjMask = IMask.createMask({
  mask: INPUT_MASKS.CPNJ,
});

export const creditCardMask = IMask.createMask({
  mask: INPUT_MASKS.CREDIT_CARD,
});

export const phoneMask = IMask.createMask({
  mask: INPUT_MASKS.PHONE_NUMBER,
});

export const residentialPhoneMask = IMask.createMask({
  mask: INPUT_MASKS.RESIDENTIAL_PHONE_NUMBER,
});

export const zipCodeMask = IMask.createMask({
  mask: INPUT_MASKS.ZIPCODE,
});

export const creditCardExpiresMask = IMask.createMask({
  mask: INPUT_MASKS.CREDIT_CARD_EXPIRES,
});

export const hourMask = IMask.createMask({
  mask: INPUT_MASKS.HOUR,
});

/**
 * Returns the CPF formatted, using this model: `000.000.000-00`.
 * @param cpf - String
 */
export const maskCpf = (cpf?: string | null) => cpfMask.resolve(cpf ?? '');

/**
 * Returns the CNPJ formatted, using this model: `00.000.000/0000-00`.
 * @param cnpj - String
 */
export const maskCNPJ = (cnpj?: string | null) => cnpjMask.resolve(cnpj ?? '');

/**
 * Returns formatted credit card, using this model: `0000-0000-0000-0000-0000`.
 * @param {string} expires
 */
export const maskCreditCardExpires = (expires: string) =>
  creditCardExpiresMask.resolve(expires ?? '');

/**
 * Returns formatted credit card expires date, using this model: `00/00`.
 * @param creditCard - String
 */
export const maskCreditCard = (creditCard: string) =>
  creditCardMask.resolve(creditCard ?? '');

/**
 * Returns formatted brazilian zip code, using this model: `00000-000`.
 * @param {string} zipCode
 */
export const maskCep = (zipCode?: string) => zipCodeMask.resolve(zipCode ?? '');

/**
 * Returns formatted brazilian phone number, using this model: `(00) 00000-0000`.
 * @param {string} phone
 */
export const maskPhone = (phone: string) => phoneMask.resolve(phone ?? '');

/**
 * Returns formatted brazilian residential phone number, using this model: `(00) 0000-0000`.
 *
 * @param {string} phone
 */
export const maskResidentialPhone = (phone: string) =>
  residentialPhoneMask.resolve(phone ?? '');

/**
 * Returns formatted date, using this model: 'dd/MM/yyyy'.
 *
 * @param {(Date | string | null)} date
 * @param {boolean} [utc]
 * @return {*}  {string}
 */
export const maskDate = (date: Date | string | null, utc?: boolean): string => {
  if (!date) {
    return '';
  }

  if (utc) {
    return new Date(date).toLocaleDateString('pt-BR', {
      timeZone: 'UTC',
    });
  }

  return format(new Date(date), 'dd/MM/yyyy');
};

/**
 * Returns formatted hour number, using this model: `00:00`.
 * @param hour - String
 */
export const maskHour = (hour: string) => hourMask.resolve(hour ?? '');

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const formatCurrency = (value, localeConfig, currencyName) => {
  const numberConfig = localeConfig.formats.number[currencyName];
  const formatter = new global.Intl.NumberFormat(
    localeConfig.locale,
    numberConfig
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return formatter.format(Big(value));
};

/**
 * Format hour to HH:mm pattern
 *
 * @param {(string | number | Date)} date
 * @return {*}
 */
export const formatHour = (date: string | number | Date) => {
  return format(new Date(date), 'HH:mm');
};

/**
 * Format date to BR pattern dd/MM/yyyy
 *
 * @param {(string | number | Date)} date
 * @return {*}
 */
export const formatDateToBR = (date: string | number | Date) => {
  return format(new Date(date), 'dd/LL/uuuu');
};

/**
 * mask an hour from start-end interval
 *
 * @param {{
 *   start: string;
 *   end: string;
 * }} {
 *   start,
 *   end,
 * }
 * @return {*}  {string}
 */
export const maskHourInterval = (interval: {
  start: string;
  end: string;
}): string => {
  if (!interval?.start && !interval?.end) {
    return '';
  }
  return `${maskHour(interval.start)} - ${maskHour(interval.end)}`;
};
